/**
 * GoalInitiativesPanel - Reusable panel for Goal Initiatives management
 * 
 * Used in:
 * - GoalDetailModal (edit mode)
 * - GoalMergeModal (source view + merged form)
 * 
 * Props:
 * - linkedInitiatives: array of initiative objects
 * - allProductInitiatives: array of all available initiatives for linking
 * - onLink: (initiative) => void
 * - onUnlink: (initiativeId) => void
 * - onCreateNew: (title) => Promise<initiative>
 * - onOpenInitiative: (initiative) => void
 * - isEditing: boolean (controls whether initiatives can be linked/unlinked)
 * - isLoading: boolean
 * - colors: theme colors from useSafeMode
 * - computedMetrics: optional computed metrics for progress display
 * - canEdit: permission check
 * - mode: 'edit' | 'create' | 'view'
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { 
  Search, Rocket, Plus, X, 
  Loader2, AlertTriangle, ExternalLink 
} from 'lucide-react';

// Status options (shared with Goal)
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'active', label: 'Active', color: 'bg-blue-500' },
  { value: 'achieved', label: 'Achieved', color: 'bg-green-500' },
  { value: 'abandoned', label: 'Abandoned', color: 'bg-red-500' },
];

const GoalInitiativesPanel = ({
  linkedInitiatives = [],
  allProductInitiatives = [],
  onLink,
  onUnlink,
  onCreateNew,
  onOpenInitiative,
  isEditing = true,
  isLoading = false,
  colors,
  computedMetrics = null,
  canEdit = true,
  mode = 'edit',
}) => {
  // Initiative search state
  const [initiativeSearch, setInitiativeSearch] = useState('');
  const [showInitiativeDropdown, setShowInitiativeDropdown] = useState(false);
  
  // Inline create initiative state
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [newInitiativeTitle, setNewInitiativeTitle] = useState('');
  const [creatingInitiative, setCreatingInitiative] = useState(false);
  
  // Ref for dropdown click-outside handling
  const initiativeDropdownRef = useRef(null);
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (initiativeDropdownRef.current && !initiativeDropdownRef.current.contains(event.target)) {
        setShowInitiativeDropdown(false);
        setShowInlineCreate(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLinkInitiative = (initiative) => {
    if (onLink) {
      onLink(initiative);
    }
    setShowInitiativeDropdown(false);
    setInitiativeSearch('');
  };

  const handleUnlinkInitiative = (initiativeId) => {
    if (onUnlink) {
      onUnlink(initiativeId);
    }
  };

  const handleInlineCreateInitiative = async () => {
    if (!newInitiativeTitle.trim() || !onCreateNew) return;
    
    setCreatingInitiative(true);
    try {
      await onCreateNew(newInitiativeTitle.trim());
      setNewInitiativeTitle('');
      setShowInlineCreate(false);
      setShowInitiativeDropdown(false);
    } catch (err) {
      console.error('Failed to create initiative:', err);
    } finally {
      setCreatingInitiative(false);
    }
  };

  // Filter initiatives that aren't already linked
  const availableInitiatives = allProductInitiatives.filter(init => 
    !linkedInitiatives.some(li => li.initiative_id === init.initiative_id) &&
    (!initiativeSearch || init.title?.toLowerCase().includes(initiativeSearch.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Execution Progress - Summary at top (only in edit mode with computed data) */}
      {mode === 'edit' && computedMetrics && (
        <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
          <div className="flex items-center justify-between mb-2">
            <span className={cn("text-sm font-medium", colors.textPrimary)}>Execution Progress</span>
            <span className={cn("text-sm", colors.textSecondary)}>{computedMetrics.execution_progress || 0}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${computedMetrics.execution_progress || 0}%` }}
            />
          </div>
          {computedMetrics.blocked_count > 0 && (
            <div className="flex items-center gap-1 mt-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs">{computedMetrics.blocked_count} initiative(s) blocked</span>
            </div>
          )}
          {/* Investment Allocation */}
          <div className={cn("mt-3 pt-3 border-t", colors.borderPrimary)}>
            <span className={cn("text-sm", colors.textSecondary)}>Investment Allocation: </span>
            <span className={cn("text-sm font-medium", colors.textPrimary)}>
              {computedMetrics.feature_count} features ({computedMetrics.investment_pct}% of portfolio)
            </span>
          </div>
        </div>
      )}
      
      {/* Add Initiative Search - Inline pattern (only when editing) */}
      {canEdit && isEditing && mode === 'edit' && (
        <div className="relative" ref={initiativeDropdownRef}>
          <Label className={cn("mb-1 block", colors.textSecondary)}>
            Link Initiative
          </Label>
          <div className="relative">
            <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
            <Input
              type="text"
              placeholder="Search existing initiatives or create new..."
              value={initiativeSearch}
              onChange={(e) => {
                setInitiativeSearch(e.target.value);
                setShowInitiativeDropdown(true);
              }}
              onFocus={() => setShowInitiativeDropdown(true)}
              className={cn("pl-10", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              data-testid="initiative-search-input"
            />
          </div>
          
          {/* Initiative Dropdown */}
          {showInitiativeDropdown && (
            <div className={cn(
              "absolute z-50 w-full mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg",
              colors.bgCard, colors.borderPrimary
            )}>
              {/* Inline Create Form */}
              {showInlineCreate ? (
                <div className={cn("p-3 border-b", colors.borderPrimary)}>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="Initiative title..."
                      value={newInitiativeTitle}
                      onChange={(e) => setNewInitiativeTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newInitiativeTitle.trim()) {
                          handleInlineCreateInitiative();
                        } else if (e.key === 'Escape') {
                          setShowInlineCreate(false);
                          setNewInitiativeTitle('');
                        }
                      }}
                      autoFocus
                      disabled={creatingInitiative}
                      className={cn("flex-1 h-8 text-sm", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                      data-testid="new-initiative-title-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleInlineCreateInitiative}
                      disabled={!newInitiativeTitle.trim() || creatingInitiative}
                      className="h-8 px-3"
                    >
                      {creatingInitiative ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Plus className="w-3 h-3" />
                      )}
                    </Button>
                    <button
                      onClick={() => {
                        setShowInlineCreate(false);
                        setNewInitiativeTitle('');
                      }}
                      className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className={cn("text-xs mt-1", colors.textMuted)}>
                    Press Enter to create, Esc to cancel
                  </p>
                </div>
              ) : onCreateNew && (
                <button
                  onClick={() => setShowInlineCreate(true)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b",
                    colors.borderPrimary, "text-indigo-600 dark:text-indigo-400"
                  )}
                >
                  <Plus className="w-4 h-4" />
                  <span className="font-medium">Create New Initiative</span>
                </button>
              )}
              
              {/* Existing Initiatives */}
              {availableInitiatives.map(init => {
                const statusConfig = STATUS_OPTIONS.find(s => s.value === init.status) || STATUS_OPTIONS[0];
                return (
                  <button
                    key={init.initiative_id}
                    onClick={() => handleLinkInitiative(init)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                      colors.textPrimary
                    )}
                  >
                    <Rocket className="w-4 h-4 text-indigo-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{init.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={cn("text-xs px-1.5 py-0.5 rounded", statusConfig.color, "text-white")}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                    <Plus className={cn("w-4 h-4", colors.textMuted)} />
                  </button>
                );
              })}
              
              {availableInitiatives.length === 0 && !showInlineCreate && (
                <div className={cn("p-4 text-center", colors.textMuted)}>
                  <p className="text-sm">All initiatives are already linked</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Linked Initiatives List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
        </div>
      ) : linkedInitiatives.length === 0 ? (
        <div className={cn("text-center py-8 rounded-lg border", colors.borderPrimary)}>
          <Rocket className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
          <p className={cn("text-sm", colors.textMuted)}>No initiatives linked yet</p>
          {isEditing && (
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              Use the search above to link existing initiatives or create a new one
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {linkedInitiatives.map(init => {
            const statusConfig = STATUS_OPTIONS.find(s => s.value === init.status) || STATUS_OPTIONS[0];
            
            return (
              <div 
                key={init.initiative_id}
                className={cn("p-3 rounded-lg border flex items-center justify-between", colors.borderPrimary, colors.bgTertiary)}
                data-testid={`linked-initiative-${init.initiative_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", "bg-indigo-100 dark:bg-indigo-900/30")}>
                    <Rocket className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div>
                    <p className={cn("font-medium", colors.textPrimary)}>{init.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={cn("text-xs px-2 py-0.5 rounded-full", statusConfig.color, "text-white")}>
                        {statusConfig.label}
                      </span>
                      {init.computed_progress !== undefined && (
                        <span className={cn("text-xs", colors.textMuted)}>
                          {init.computed_progress}% complete
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {onOpenInitiative && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onOpenInitiative(init)}
                      title="Open initiative"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                  {canEdit && isEditing && mode === 'edit' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUnlinkInitiative(init.initiative_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Unlink initiative"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalInitiativesPanel;
